import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Zap, TrendingUp, ShieldCheck, FileText, TrendingDown, Loader2 } from "lucide-react";

const TIER_META = {
  quick:       { label: "Quick",       icon: Zap,          tone: "emerald" },
  recommended: { label: "Recommended", icon: TrendingUp,   tone: "violet"  },
  max_safe:    { label: "Max Safe",    icon: ShieldCheck,  tone: "amber"   },
  manual:      { label: "Manual",      icon: FileText,     tone: "slate"   },
};

const TONE_CLASSES = {
  emerald: { border: "border-emerald-300", bg: "bg-emerald-50",  text: "text-emerald-900",  chip: "bg-emerald-100 text-emerald-800" },
  violet:  { border: "border-violet-400",  bg: "bg-violet-50",   text: "text-violet-900",   chip: "bg-violet-100 text-violet-800"   },
  amber:   { border: "border-amber-300",   bg: "bg-amber-50",    text: "text-amber-900",    chip: "bg-amber-100 text-amber-800"     },
  slate:   { border: "border-slate-300",   bg: "bg-slate-50",    text: "text-slate-900",    chip: "bg-slate-100 text-slate-800"     },
};

/**
 * TierHistoryCard — shows how many offers of each tier the user has sent,
 * their conversion rate to signed, and the average price + % of ARV. Lives
 * at the top of the Offers page so investors see which tier converts best.
 */
export default function TierHistoryCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/offers/tier-stats");
        setStats(data);
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="card-flat p-5 flex items-center gap-2 text-sm text-muted-foreground"
           data-testid="tier-history-loading">
        <Loader2 size={14} className="animate-spin" /> Loading tier history…
      </div>
    );
  }
  if (!stats || stats.total_sent === 0) {
    return (
      <div className="card-flat p-5" data-testid="tier-history-empty">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-slate-700" />
          <h3 className="font-display text-lg font-bold">Tier Conversion History</h3>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Send offers from the Smart Offer Recommender or Dialer to start
          seeing which tier converts best. Every tier you pick is logged so
          FlipPilot learns your winning pattern.
        </p>
      </div>
    );
  }

  const tierKeys = ["quick", "recommended", "max_safe", "manual"];
  // Find the highest conversion tier to crown the winner
  const winner = tierKeys.reduce((best, k) => {
    const s = stats[k];
    if (!s || s.sent === 0) return best;
    if (!best || s.conversion_pct > best.pct) return { key: k, pct: s.conversion_pct };
    return best;
  }, null);

  return (
    <div className="card-flat p-5 md:p-6" data-testid="tier-history-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-slate-700" />
            <h3 className="font-display text-lg md:text-xl font-bold">
              Tier Conversion History
            </h3>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Which Smart-Offer tier actually gets seller signatures — {stats.total_sent} offer{stats.total_sent === 1 ? "" : "s"} sent
          </p>
        </div>
        {winner && winner.pct > 0 && (
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-full px-2.5 py-1"
               data-testid="tier-history-winner">
            🏆 {TIER_META[winner.key].label} wins · {winner.pct}%
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="tier-history-grid">
        {tierKeys.map((k) => {
          const s = stats[k];
          if (!s) return null;
          const meta = TIER_META[k];
          const cls = TONE_CLASSES[meta.tone];
          const Icon = meta.icon;
          const isEmpty = s.sent === 0;
          return (
            <div
              key={k}
              data-testid={`tier-history-${k}`}
              className={`rounded-2xl border ${cls.border} ${cls.bg} p-4 ${isEmpty ? "opacity-50" : ""}`}
            >
              <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest ${cls.text}`}>
                <Icon size={11} strokeWidth={2.4} />
                {meta.label}
              </div>
              <div className={`font-display font-bold text-2xl mt-1 ${cls.text}`}>
                {s.sent}
              </div>
              <div className="text-[10px] text-slate-600">
                offer{s.sent === 1 ? "" : "s"} sent
              </div>

              <div className="mt-3 pt-3 border-t border-slate-200/70 space-y-1 text-[11px]">
                <Row label="Signed" value={
                  <span className={`font-mono font-semibold ${cls.text}`}>
                    {s.signed}
                  </span>
                } />
                <Row label="Convert" value={
                  <span className={`font-mono font-semibold inline-flex items-center gap-1 ${
                    s.conversion_pct >= 30 ? "text-emerald-700" :
                    s.conversion_pct >= 10 ? "text-amber-700" :
                    "text-slate-500"
                  }`}>
                    {s.conversion_pct}%
                    {s.conversion_pct < 10 && s.sent >= 5 &&
                      <TrendingDown size={10} strokeWidth={2.4} />}
                  </span>
                } />
                {s.avg_price > 0 && (
                  <Row label="Avg price" value={
                    <span className="font-mono">{money(s.avg_price)}</span>
                  } />
                )}
                {s.avg_arv_pct > 0 && (
                  <Row label="Avg ARV" value={
                    <span className="font-mono">{s.avg_arv_pct}%</span>
                  } />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{label}</span>
      {value}
    </div>
  );
}
